import { ResponsiveOverlay } from '@/components/responsive-overlay';
import { useState } from 'react';
import { Icons } from '@/components/primitives';
import { reportActionError } from '@/lib/report-action-error';
import { isLaunchRevisionConflict, LAUNCH_REVISION_CHANGED_MESSAGE } from '@/api/manage-hooks';
import { LockGlyph } from '@/components/lock-primitives';
import { CHANNEL_FORMATS } from './creative-config';
import { fmtDay } from '@/lib/datetime';
import type { Channel } from '@/api/types';

/**
 * The publishing handoff confirmation.
 *
 * It owns the launch attempt's outcome because of what it promises in its own
 * subtitle: the window stays open until the destination confirms success or
 * failure. The wizard used to close it from inside the launch's catch, so the
 * single moment this dialog exists for — a launch that threw — was the moment
 * it vanished, leaving a banner behind the wizard, no retry, and a campaign the
 * server may well have accepted (a lost or timed-out response on an accepted
 * launch leaves `ready_to_publish` in D1 while provider sync is already
 * queued). A failure now renders here with the same button ready to try again,
 * and closing hands the message up so it survives as the screen's banner.
 *
 * `onConfirm` is expected to throw: the screen closes this dialog by unmounting
 * it on success, and does nothing else.
 */
export interface CampaignLaunchDialogProps {
  /** The saved campaign being handed off; failures are reported against it. */
  campaignId: string;
  campaignName: string;
  /** Null only before a channel is picked, which the wizard never launches. */
  channel: Channel | null;
  /** The slots' audience types, e.g. `customer + lookalike`. */
  audienceTypeSummary: string;
  audienceSlotCount: number;
  markup: boolean;
  /** The campaign window, `YYYY-MM-DD`. */
  startDate: string;
  endDate: string;
  /** Resolves when the launch was accepted; throws when it was not. */
  onConfirm: () => Promise<void>;
  /** Called with the failure message still showing, or null when there is none. */
  onDismiss: (error: string | null) => void;
}

export function CampaignLaunchDialog({
  campaignId,
  campaignName,
  channel,
  audienceTypeSummary,
  audienceSlotCount,
  markup,
  startDate,
  endDate,
  onConfirm,
  onDismiss,
}: CampaignLaunchDialogProps) {
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const confirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm();
    } catch (err) {
      // A draft that changed after it was reviewed is refused, not launched: say what to do.
      setError(isLaunchRevisionConflict(err) ? LAUNCH_REVISION_CHANGED_MESSAGE : reportActionError(err, {
        operation: 'campaign.mark_ready',
        context: { entity_kind: 'campaign', entity_id: campaignId },
      }));
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <ResponsiveOverlay label="Publishing progress" kind="progress" className="progress-host" dismissible={!submitting} onClose={() => onDismiss(error)}>
      <div className="launch-dialog">
        <div className="launch-dialog-hd">
          <h3>Publish this campaign?</h3>
          <div className="sub">
            This locks <b style={{color:'var(--ink)'}}>{campaignName || 'this campaign'}</b> in <b style={{color:'var(--ink)'}}>ready_to_publish</b> and queues its one {channel === 'meta' ? 'Meta' : 'TTD'} destination when provider publishing is enabled. This window stays open until the destination confirms success or failure.
          </div>
        </div>
        <div className="launch-dialog-body">
          <div className="lbl">Locks at the publishing handoff</div>
          <div className="launch-dialog-list">
            <div className="item">
              <span className="item-ico"><LockGlyph/></span>
              <span><b>Channel:</b> {channel ? CHANNEL_FORMATS[channel].name : '—'}</span>
            </div>
            <div className="item">
              <span className="item-ico"><LockGlyph/></span>
              <span><b>Audience type:</b> {audienceTypeSummary} <span className="muted">· {audienceSlotCount} separate reportable delivery group{audienceSlotCount === 1 ? '' : 's'} planned</span></span>
            </div>
            <div className="item">
              <span className="item-ico"><LockGlyph/></span>
              <span><b>Agency margin:</b> {markup ? (channel === 'meta' ? '15% of gross' : channel === 'ctv' ? '$25 → $30 reported CPM' : channel === 'dsp' ? '$6 → $8 reported CPM' : 'On') : 'Off · base-rate pricing'}</span>
            </div>
            <div className="item">
              <span className="item-ico"><LockGlyph/></span>
              <span><b>Campaign dates:</b> {fmtDay(startDate)} → {fmtDay(endDate)} <span className="muted">· duplicate the campaign to change them later</span></span>
            </div>
            {channel === 'meta' && (
              <div className="item">
                <span className="item-ico"><LockGlyph/></span>
                <span><b>Meta delivery contract:</b> Campaign, Ad Set, audience, creative, and Ad defaults are enforced server-side.</span>
              </div>
            )}
          </div>
          <div style={{fontSize:11.5,color:'var(--ink-2)',marginTop:12,lineHeight:1.4}}>
            The exact audience revisions, creative selections, flights, budget, and one delivery group per audience are saved with the campaign and published to the selected destination. If provider publishing is disabled, the campaign remains locally ready without creating provider objects.
          </div>
          {error && (
            <div role="alert" style={{color:'var(--bad)',fontSize:11.5,marginTop:12,lineHeight:1.4}}>
              Publish failed: {error}
              <div className="muted" style={{marginTop:6}}>
                A request that never answered may already have been accepted, so this
                campaign may already be queued. Trying again is safe — a campaign
                already in <b>ready_to_publish</b> is refused rather than published twice —
                and closing this window keeps the message on the campaign.
              </div>
            </div>
          )}
        </div>
        <div className="launch-dialog-foot">
          <button
            className="btn"
            disabled={submitting}
            onClick={() => onDismiss(error)}
          >
            {error ? 'Close' : 'Cancel'}
          </button>
          <button
            className="btn btn-primary"
            disabled={submitting}
            onClick={() => { void confirm(); }}
          >
            {Icons.check} {submitting ? 'Queueing…' : error ? 'Try publishing again' : 'Publish campaign'}
          </button>
        </div>
      </div>
    </ResponsiveOverlay>
  );
}
